import type { TransformItems } from 'instantsearch.js';
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import { useCallback } from 'react';
import { useRefinementList } from 'react-instantsearch';

import type { ParentFacet } from '~/types/algolia';
import { MATERIAL_GROUP, PERSONALISED_PRICE, PRICE } from '~/utilities/constants/algolia';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';

export const useAlgoliaFacetOptions = ({ attribute, valueEN }: ParentFacet) => {
  const { visibleFacetsList, categoryDetails: { visibleFacetsV2 } = {} } = useProductListContext();

  const transformItems: TransformItems<RefinementListItem> = useCallback(
    refinementListItem => {
      const solrFacetValues = visibleFacetsV2?.find(facet =>
        facet.code.includes(valueEN)
      )?.facetValues;

      if (solrFacetValues) {
        return solrFacetValues.reduce((sortedRefinements, current) => {
          // `value` is escaped (`-20%` becomes `\-20%`), `label` holds the raw facet name
          const algoliaFacetValue = refinementListItem.find(facet => facet.label === current.name);

          if (algoliaFacetValue) {
            sortedRefinements.push(algoliaFacetValue);
          }

          return sortedRefinements;
        }, [] as RefinementListItem[]);
      }

      return refinementListItem;
    },
    [visibleFacetsV2, valueEN]
  );

  const { items, refine, canRefine } = useRefinementList({
    attribute,
    limit: 100,
    escapeFacetValues: true,
    transformItems,
  });

  const showFacet = useCallback(() => {
    if (valueEN === PRICE || valueEN === PERSONALISED_PRICE) {
      return true;
    }

    if (visibleFacetsList.size > 0 && items.length > 1) {
      return visibleFacetsList.has(valueEN);
    }

    if (valueEN === MATERIAL_GROUP && items.length === 1) {
      return items.some(item => item.isRefined);
    }

    return items.length > 1;
  }, [items, valueEN, visibleFacetsList]);

  return { items, refine, canRefine, showFacet };
};
